import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import { BRAND, GRADIENT, LINE, EASE, FONT, GLASS, SHADOW } from "./theme/brand";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Layout from "./components/Layout";
import Databases from "./pages/Databases";
import Settings from "./pages/Settings";
import Terminal from "./pages/Terminal";
import Defense from "./pages/Defense";

// ─────────────────────────────────────────────────────────────────────────────
// ScalaVisione tasarım sistemi — MUI teması
//
// Ana site (ScalaVisioneTRV2) Tailwind ile yazılı; panel MUI. Paneli Tailwind'e
// taşımak yerine sitenin tasarım gramerini bu temaya çevirdik:
//
//   • Pill (tam yuvarlak) butonlar, cyan→azure gradyan üzerinde KOYU metin
//   • Cam yüzeyler: 150° iki duraklı gradyan + blur(18px) saturate(130%)
//   • Saç teli kenarlıklar (ink %9), mono etiketler, tabular rakamlar
//   • Sıkı başlık harf aralığı (-0.046em) ve dar satır yüksekliği
//   • Tek renk ailesinde kalan vurgu gradyanı (cyan → azure)
//
// Ölçekler panel bağlamı için küçültüldü; renkler birebir korundu.
// ─────────────────────────────────────────────────────────────────────────────

const sheen = {
  content: '""',
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "-40%",
  width: "28%",
  background: "rgba(255,255,255,0.25)",
  filter: "blur(10px)",
  opacity: 0,
  pointerEvents: "none",
};

export const visioneTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: BRAND.void,
      paper: "rgba(14, 19, 32, 0.82)",
    },
    primary: { main: BRAND.azure, light: BRAND.cyan, dark: BRAND.deep },
    secondary: { main: BRAND.ember, light: BRAND.flare },
    success: { main: BRAND.mint },
    error: { main: BRAND.rose },
    warning: { main: BRAND.flare },
    info: { main: BRAND.cyan },
    divider: LINE.hairline,
    text: {
      primary: BRAND.ink,
      secondary: BRAND.inkDim,
      disabled: BRAND.inkFaint,
    },
  },

  typography: {
    fontFamily: FONT.sans,
    // Sitedeki text-display / text-h2 / text-h3 ölçeğinin panel karşılıkları.
    h3: { fontWeight: 600, letterSpacing: "-0.05em", lineHeight: 0.95 },
    h4: { fontWeight: 600, letterSpacing: "-0.046em", lineHeight: 1.0 },
    h5: { fontWeight: 600, letterSpacing: "-0.038em", lineHeight: 1.08 },
    h6: { fontWeight: 600, letterSpacing: "-0.028em", lineHeight: 1.15 },
    body1: { fontSize: "0.9375rem", letterSpacing: "-0.011em" },
    body2: { fontSize: "0.875rem", letterSpacing: "-0.008em" },
    caption: { fontSize: "0.75rem", letterSpacing: "0.005em" },
    overline: {
      fontFamily: FONT.mono,
      fontSize: "0.6875rem",
      letterSpacing: "0.2em",
      fontWeight: 500,
    },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "-0.005em" },
  },

  shape: { borderRadius: 16 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BRAND.void,
          // Sitedeki dekoratif ışık lekeleri + ince ızgara dokusu.
          backgroundImage: `radial-gradient(circle at 10% 8%, rgba(29,111,242,0.10), transparent 24%),
             radial-gradient(circle at 92% 22%, rgba(45,167,255,0.07), transparent 22%),
             linear-gradient(to right, rgba(234,240,248,0.035) 1px, transparent 1px),
             linear-gradient(to bottom, rgba(234,240,248,0.035) 1px, transparent 1px)`,
          backgroundSize: "auto, auto, 72px 72px, 72px 72px",
          backgroundAttachment: "fixed",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          background: GLASS.background,
          backdropFilter: GLASS.backdropFilter,
          WebkitBackdropFilter: GLASS.backdropFilter,
          border: `1px solid ${LINE.hairline}`,
          boxShadow: SHADOW.card,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          position: "relative",
          overflow: "hidden",
          borderRadius: 999, // sitedeki pill biçim
          padding: "9px 20px",
          minHeight: 40,
          transition: `all 0.3s ${EASE.outExpo}`,
          "&:active": { transform: "scale(0.98)" },
          "&:hover::after": { opacity: 1, animation: `sv-sheen 0.9s ${EASE.outExpo}` },
          "&::after": sheen,
        },
        sizeSmall: { padding: "5px 14px", minHeight: 32, fontSize: "0.8125rem" },
        sizeLarge: { padding: "13px 28px", minHeight: 52, fontSize: "0.95rem" },
        contained: {
          // Gradyanın üzerinde koyu metin — sitedeki birincil buton.
          background: GRADIENT.brand,
          color: "#03070f",
          boxShadow: SHADOW.cyan,
          "&:hover": { boxShadow: SHADOW.cyanHover, transform: "translateY(-1px)" },
          "&.Mui-disabled": { background: "rgba(234,240,248,0.08)", color: BRAND.inkFaint },
        },
        outlined: {
          borderColor: "rgba(234,240,248,0.18)",
          backgroundColor: "rgba(234,240,248,0.03)",
          color: BRAND.ink,
          "&:hover": {
            borderColor: `${BRAND.cyan}99`,
            backgroundColor: "rgba(45,167,255,0.07)",
          },
        },
        text: { color: BRAND.inkDim, "&:hover": { color: BRAND.cyan, backgroundColor: "transparent" } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: `all 0.25s ${EASE.outExpo}`,
          "&:hover": { backgroundColor: "rgba(45,167,255,0.10)" },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontFamily: FONT.mono,
          fontSize: "0.6875rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 500,
          height: 24,
          border: "1px solid rgba(234,240,248,0.12)",
          backgroundColor: "rgba(234,240,248,0.04)",
          color: BRAND.inkDim,
        },
        outlined: { backgroundColor: "transparent" },
        label: { paddingInline: 10 },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "rgba(234,240,248,0.03)",
          fontSize: "0.875rem",
          transition: `all 0.2s ${EASE.outExpo}`,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: LINE.hairline },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(234,240,248,0.18)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${BRAND.cyan}8c`,
            borderWidth: 1,
          },
          "&.Mui-focused": { boxShadow: `0 0 0 3px rgba(45,167,255,0.14)` },
        },
        input: { "&::placeholder": { color: BRAND.inkFaint, opacity: 1 } },
      },
    },

    MuiInputLabel: { styleOverrides: { root: { fontSize: "0.875rem", color: BRAND.inkFaint } } },

    MuiSelect: { styleOverrides: { select: { fontSize: "0.875rem" } } },

    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 14, boxShadow: SHADOW.raised },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          fontSize: "0.875rem",
          "&.Mui-selected": { backgroundColor: "rgba(45,167,255,0.12)" },
        },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: LINE.hairline } } },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: BRAND.surface,
          border: `1px solid ${LINE.hairline}`,
          borderRadius: 10,
          fontSize: "0.75rem",
          fontFamily: FONT.mono,
          letterSpacing: "0.02em",
          boxShadow: SHADOW.raised,
        },
        arrow: { color: BRAND.surface },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: "16px !important",
          border: `1px solid ${LINE.hairline}`,
          transition: `all 0.3s ${EASE.outExpo}`,
          "&:before": { display: "none" },
          "&:hover": { borderColor: "rgba(234,240,248,0.14)" },
        },
      },
    },

    MuiDialog: { styleOverrides: { paper: { borderRadius: 20, boxShadow: SHADOW.raised } } },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 14, fontSize: "0.875rem", border: "1px solid" },
        standardError: {
          backgroundColor: "rgba(255,107,129,0.10)",
          borderColor: "rgba(255,107,129,0.30)",
          color: BRAND.rose,
        },
        standardSuccess: {
          backgroundColor: "rgba(61,220,151,0.10)",
          borderColor: "rgba(61,220,151,0.30)",
          color: BRAND.mint,
        },
        standardWarning: {
          backgroundColor: "rgba(255,161,77,0.10)",
          borderColor: "rgba(255,161,77,0.28)",
          color: BRAND.flare,
        },
        standardInfo: {
          backgroundColor: "rgba(45,167,255,0.09)",
          borderColor: "rgba(45,167,255,0.26)",
          color: BRAND.cyan,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 6, backgroundColor: "rgba(234,240,248,0.08)" },
        bar: { borderRadius: 999, background: GRADIENT.brand },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: LINE.hairline, fontSize: "0.875rem" },
        head: {
          fontFamily: FONT.mono,
          fontSize: "0.6875rem",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: BRAND.inkFaint,
          fontWeight: 500,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(5, 7, 12, 0.86)",
          backgroundImage: "none",
          backdropFilter: "blur(20px) saturate(130%)",
          WebkitBackdropFilter: "blur(20px) saturate(130%)",
          borderRight: `1px solid ${LINE.hairline}`,
          boxShadow: "none",
        },
      },
    },

    MuiAppBar: { styleOverrides: { root: { backgroundImage: "none", boxShadow: "none" } } },
  },
});

// Sadece giriş yapanların görebileceği rotaları koruyan bileşen.
// NOT: Bu yalnızca bir arayüz kolaylığıdır - gerçek koruma backend'deki JWT
// kontrolüdür (src/middleware/auth.js).
const PrivateRoute = ({ children }) => {
  const { token } = useContext(AuthContext);
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

function AppContent() {
  return (
    <ThemeProvider theme={visioneTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/defense"
            element={
              <PrivateRoute>
                <Defense />
              </PrivateRoute>
            }
          />
          <Route
            path="/databases"
            element={
              <PrivateRoute>
                <Databases />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/terminal"
            element={
              <PrivateRoute>
                <Terminal />
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
